import React, { useState } from "react";
import {
  EuiTitle,
  EuiText,
  EuiIcon,
  EuiSpacer,
  EuiBadge,
  EuiHorizontalRule,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";

interface Feature {
  icon: string;
  title: string;
  description: string;
  badge?: string;
  details?: string[];
}

interface FeatureCategory {
  id: string;
  title: string;
  icon: string;
  color: string;
  features: Feature[];
}

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  cardBgHover: "var(--ak-surface-2)",
  subtleBg: "var(--ak-surface-2)",
  border: "var(--ak-border)",
  borderHover: "var(--ak-accent-glow)",
  borderAccent: "var(--ak-accent-border)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  success: "var(--ak-success)",
  radius: 12,
  radiusSm: 8,
  sans: "system-ui,-apple-system,sans-serif",
};

export function useIsMobile(breakpoint = 600): boolean {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: C.accent,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const FeatureIcon: React.FC<{ type: string }> = ({ type }) => (
  <div
    style={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: 10,
      background: `linear-gradient(135deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <EuiIcon type={type} size="m" color="ghost" />
  </div>
);

const FeatureRow: React.FC<{ feature: Feature }> = ({ feature }) => {
  const [hovered, setHovered] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? C.cardBgHover : C.cardBg,
        border: `1px solid ${hovered ? C.borderHover : C.border}`,
        borderRadius: C.radius,
        padding: isMobile ? "16px" : "20px 22px",
        transition: "all 0.2s ease",
        transform: hovered ? "translateY(-1px)" : "none",
        boxShadow: hovered
          ? "0 4px 16px var(--ak-accent-tint-strong)"
          : "0 1px 4px var(--ak-shadow)",
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {!isMobile && <FeatureIcon type={feature.icon} />}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "4px 10px",
              marginBottom: 6,
            }}
          >
            {isMobile && (
              <div
                style={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  borderRadius: 6,
                  background: `linear-gradient(135deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EuiIcon type={feature.icon} size="s" color="ghost" />
              </div>
            )}
            <span
              style={{ fontSize: "0.92rem", fontWeight: 700, color: C.accent }}
            >
              {feature.title}
            </span>
            {feature.badge && (
              <EuiBadge
                style={{
                  background: C.accentLight,
                  color: C.accent,
                  border: `1px solid ${C.accentBorder}`,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  padding: "1px 8px",
                }}
              >
                {feature.badge}
              </EuiBadge>
            )}
          </div>

          <EuiText size="s" color="subdued">
            <p style={{ margin: 0, lineHeight: 1.65 }}>{feature.description}</p>
          </EuiText>

          {feature.details && feature.details.length > 0 && (
            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: `1px solid ${C.border}`,
                display: "flex",
                flexWrap: "wrap",
                gap: isMobile ? "6px 0" : "8px 20px",
              }}
            >
              {feature.details.map((detail, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 6,
                    width: isMobile ? "100%" : "auto",
                    minWidth: isMobile ? 0 : 180,
                    maxWidth: "100%",
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      marginTop: 1,
                      borderRadius: "50%",
                      background: "rgba(0,117,110,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <EuiIcon type="check" color="success" size="s" />
                  </div>
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: C.textSecondary,
                      lineHeight: 1.5,
                    }}
                  >
                    {detail}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryAccordion: React.FC<{
  category: FeatureCategory;
  defaultOpen: boolean;
}> = ({ category, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div
      id={`opsanio-${category.id}`}
      style={{
        background: C.cardBg,
        border: `1px solid ${open ? C.borderAccent : hovered ? C.borderHover : C.border}`,
        borderRadius: C.radius,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: open
          ? "0 4px 20px var(--ak-accent-tint)"
          : "0 1px 4px var(--ak-shadow)",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 10 : 14,
          padding: isMobile ? "14px 14px" : "18px 22px",
          background: open ? C.accentLight : "transparent",
          border: "none",
          cursor: "pointer",
          transition: "background 0.18s",
          fontFamily: C.sans,
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: isMobile ? 36 : 46,
            height: isMobile ? 36 : 46,
            flexShrink: 0,
            borderRadius: C.radiusSm,
            background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}cc 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <EuiIcon
            type={category.icon}
            size={isMobile ? "m" : "l"}
            color="ghost"
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: isMobile ? "0.9rem" : "1.05rem",
              fontWeight: 700,
              color: C.accent,
              lineHeight: 1.3,
              whiteSpace: isMobile ? "normal" : "nowrap",
              overflow: isMobile ? "visible" : "hidden",
              textOverflow: isMobile ? "clip" : "ellipsis",
            }}
          >
            {category.title}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {!isMobile && (
            <span
              style={{
                fontFamily: C.sans,
                fontSize: "0.73rem",
                fontWeight: 600,
                color: C.textMuted,
                background: C.subtleBg,
                border: `1px solid ${C.border}`,
                borderRadius: 100,
                padding: "3px 10px",
                whiteSpace: "nowrap",
              }}
            >
              {category.features.length} Features
            </span>
          )}

          <div
            style={{
              width: isMobile ? 24 : 28,
              height: isMobile ? 24 : 28,
              background: open ? C.accentBorder : C.subtleBg,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              flexShrink: 0,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              style={{
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            >
              <path
                d="M2 4l4 4 4-4"
                stroke={open ? C.accent : C.textMuted}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </button>

      {open && (
        <div style={{ padding: isMobile ? "0 12px 16px" : "0 22px 22px" }}>
          <div
            style={{
              height: 2,
              marginBottom: 16,
              background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 60%, transparent 100%)`,
              borderRadius: 2,
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {category.features.map((feature, i) => (
              <FeatureRow key={i} feature={feature} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface SummaryCard {
  icon: string;
  iconColor: string;
  accentColor: string;
  titleKey: string;
  titleDefault: string;
  descKey: string;
  descDefault: string;
}

const SUMMARY_CARDS: SummaryCard[] = [
  {
    icon: "cloudDrizzle",
    iconColor: "var(--ak-accent)",
    accentColor: "var(--ak-accent-tint)",
    titleKey: "anketa.autonubil.home.appliance.feature.card.deploy",
    titleDefault: "Easy Deployment",
    descKey: "anketa.autonubil.home.appliance.feature.card.deploy.description",
    descDefault: "Deploy in minutes across your entire infrastructure.",
  },
  {
    icon: "checkInCircleFilled",
    iconColor: "var(--ak-success)",
    accentColor: "rgba(0,117,110,0.07)",
    titleKey: "anketa.autonubil.home.appliance.feature.card.compliance",
    titleDefault: "Compliance Ready",
    descKey:
      "anketa.autonubil.home.appliance.feature.card.compliance.description",
    descDefault: "Out-of-the-box compliance for major frameworks.",
  },
  {
    icon: "logstashQueue",
    iconColor: "#7c3aed",
    accentColor: "rgba(124,58,237,0.07)",
    titleKey: "anketa.autonubil.home.appliance.feature.card.integrations",
    titleDefault: "Integrations",
    descKey:
      "anketa.autonubil.home.appliance.feature.card.integrations.description",
    descDefault: "Connect with your existing security and IT stack.",
  },
];

const SummaryCardItem: React.FC<{
  card: SummaryCard;
  translate: (key: string, opts: { defaultMessage: string }) => string;
}> = ({ card, translate }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: "1 1 240px",
        minWidth: 0,
        background: C.cardBg,
        border: `1px solid ${hovered ? C.borderHover : C.border}`,
        borderRadius: C.radius,
        padding: "24px 20px",
        transition: "all 0.2s ease",
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered
          ? `0 8px 24px ${card.accentColor}`
          : "0 1px 4px var(--ak-shadow)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: card.iconColor,
          borderRadius: `${C.radius}px ${C.radius}px 0 0`,
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.2s",
        }}
      />

      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          marginBottom: 16,
          background: card.accentColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EuiIcon type={card.icon} size="xl" color={card.iconColor} />
      </div>

      <div
        style={{
          fontSize: "0.95rem",
          fontWeight: 700,
          color: C.textPrimary,
          marginBottom: 8,
        }}
      >
        {translate(card.titleKey, { defaultMessage: card.titleDefault })}
      </div>

      <EuiText size="s" color="subdued">
        <p style={{ margin: 0, lineHeight: 1.65 }}>
          {translate(card.descKey, { defaultMessage: card.descDefault })}
        </p>
      </EuiText>
    </div>
  );
};

export const ModernFeaturesPanel: React.FC = () => {
  const { translate } = useAnketaI18n();
  const i18nPrefix = "anketa.autonubil.home.appliance.feature";
  const isMobile = useIsMobile();

  const t = (key: string, defaultMessage: string) =>
    translate(key, { defaultMessage });

  const featureCategories: FeatureCategory[] = [
    {
      id: "asset-management",
      title: t(`${i18nPrefix}.assetManagement`, "Asset Management"),
      icon: "indexMapping",
      color: C.accent,
      features: [
        {
          icon: "database",
          title: t(`${i18nPrefix}.categorisation`, "Categorisation"),
          description: t(`${i18nPrefix}.categorisation.description`, ""),
          details: [
            t(`${i18nPrefix}.categorisation`, ""),
            t(`${i18nPrefix}.categorisation.tags`, ""),
            t(`${i18nPrefix}.categorisation.groups`, ""),
            t(`${i18nPrefix}.categorisation.power`, ""),
          ],
        },
        {
          icon: "apps",
          title: t(`${i18nPrefix}.asset`, "Assets"),
          description: t(`${i18nPrefix}.asset.description`, ""),
          details: [
            t(`${i18nPrefix}.asset.sources`, ""),
            t(`${i18nPrefix}.asset.cpes`, ""),
            t(`${i18nPrefix}.asset.snmp`, ""),
          ],
        },
        {
          icon: "ip",
          title: t(`${i18nPrefix}.asset.network`, "Network"),
          description: t(`${i18nPrefix}.asset.network.description`, ""),
          details: [
            t(`${i18nPrefix}.asset.network.ip`, ""),
            t(`${i18nPrefix}.asset.network.mac`, ""),
            t(`${i18nPrefix}.asset.network.nic.count`, ""),
            t(`${i18nPrefix}.asset.network.hostnames`, ""),
          ],
        },
        {
          icon: "lock",
          title: t(`${i18nPrefix}.asset.certificates`, "Certificates"),
          description: t(`${i18nPrefix}.asset.certificates.description`, ""),
          details: [
            t(`${i18nPrefix}.asset.certificates.used`, ""),
            t(`${i18nPrefix}.asset.certificates.used.state`, ""),
          ],
        },
        {
          icon: "lock",
          title: t(`${i18nPrefix}.asset.services`, "Services"),
          description: t(`${i18nPrefix}.asset.services.description`, ""),
          details: [
            t(`${i18nPrefix}.asset.services.used`, ""),
            t(`${i18nPrefix}.asset.services.ports`, ""),
            t(`${i18nPrefix}.asset.services.protocol`, ""),
          ],
        },
        {
          icon: "node",
          title: t(`${i18nPrefix}.networkFlows`, "Network Flows"),
          description: t(`${i18nPrefix}.networkFlows.description`, ""),
          details: [
            t(`${i18nPrefix}.networkFlows.volume`, ""),
            t(`${i18nPrefix}.networkFlows.connections`, ""),
            t(`${i18nPrefix}.networkFlows.protocols`, ""),
          ],
        },
        {
          icon: "securitySignal",
          title: t(`${i18nPrefix}.asset.security`, "Security"),
          description: t(`${i18nPrefix}.asset.security.description`, ""),
          details: [
            t(`${i18nPrefix}.asset.security.cve`, ""),
            t(`${i18nPrefix}.asset.security.events`, ""),
            t(`${i18nPrefix}.asset.security.links`, ""),
          ],
        },
      ],
    },
    {
      id: "software-management",
      title: t(`${i18nPrefix}.software.management`, "Software Management"),
      icon: "appManagement",
      color: C.accent,
      features: [
        {
          icon: "package",
          title: t(
            `${i18nPrefix}.software.management.title`,
            "Software Inventory",
          ),
          description: t(`${i18nPrefix}.software.management.description`, ""),
          details: [
            t(`${i18nPrefix}.software.management.name`, ""),
            t(`${i18nPrefix}.software.management.manufactory`, ""),
            t(`${i18nPrefix}.software.management.category`, ""),
            t(`${i18nPrefix}.software.management.reference`, ""),
          ],
        },
        {
          icon: "securitySignalDetected",
          title: t(`${i18nPrefix}.software.management.security`, "Security"),
          description: t(`${i18nPrefix}.software.management.security`, ""),
          details: [
            t(`${i18nPrefix}.software.management.security.cve`, ""),
            t(`${i18nPrefix}.software.management.security.cve.patches`, ""),
          ],
        },
        {
          icon: "monitor",
          title: t(
            `${i18nPrefix}.software.management.patch`,
            "Patch Management",
          ),
          description: t(
            `${i18nPrefix}.software.management.patch.description`,
            "",
          ),
          details: [
            t(`${i18nPrefix}.software.management.patch.same`, ""),
            t(`${i18nPrefix}.software.management.patch.different`, ""),
          ],
        },
        {
          icon: "stopWatch",
          title: t(`${i18nPrefix}.software.management.eol`, "End of Life"),
          description: t(
            `${i18nPrefix}.software.management.eol.description`,
            "",
          ),
          details: [
            t(`${i18nPrefix}.software.management.eol.version`, ""),
            t(`${i18nPrefix}.software.management.eol.updates`, ""),
            t(`${i18nPrefix}.software.management.eol.phase`, ""),
          ],
        },
      ],
    },
    {
      id: "vulnerability-management",
      title: t(`${i18nPrefix}.cve`, "Vulnerability Management"),
      icon: "securitySignalDetected",
      color: C.accent,
      features: [
        {
          icon: "visGauge",
          title: t(`${i18nPrefix}.cve.name`, "CVE Details"),
          description: t(`${i18nPrefix}.cve.name.description`, ""),
          details: [
            t(`${i18nPrefix}.cve.description`, ""),
            t(`${i18nPrefix}.cve.score`, ""),
            t(`${i18nPrefix}.cve.cwe`, ""),
          ],
        },
        {
          icon: "apps",
          title: t(`${i18nPrefix}.cve.components`, "Affected Comps."),
          description: t(`${i18nPrefix}.cve.components.description`, ""),
          details: [
            t(`${i18nPrefix}.cve.components.software`, ""),
            t(`${i18nPrefix}.cve.components.devices`, ""),
          ],
        },
        {
          icon: "visLinks",
          title: t(`${i18nPrefix}.cve.sources`, "Data Sources"),
          description: t(`${i18nPrefix}.cve.sources.description`, ""),
          details: [
            t(`${i18nPrefix}.cve.sources.cve`, ""),
            t(`${i18nPrefix}.cve.sources.nvd`, ""),
            t(`${i18nPrefix}.cve.sources.cwe`, ""),
            t(`${i18nPrefix}.cve.sources.manufactory`, ""),
          ],
        },
        {
          icon: "beaker",
          title: t(`${i18nPrefix}.openvas`, "OpenVAS"),
          description: t(`${i18nPrefix}.openvas.description`, ""),
          details: [
            t(`${i18nPrefix}.openvas.integration`, ""),
            t(`${i18nPrefix}.openvas.mapping`, ""),
            t(`${i18nPrefix}.openvas.report`, ""),
          ],
        },
      ],
    },
    {
      id: "service-management",
      title: t(`${i18nPrefix}.services`, "Service Management"),
      icon: "node",
      color: C.accent,
      features: [
        {
          icon: "layers",
          title: t(`${i18nPrefix}.services.information`, "Service Information"),
          description: t(`${i18nPrefix}.services.information.description`, ""),
          details: [
            t(`${i18nPrefix}.services.information.protocol`, ""),
            t(`${i18nPrefix}.services.information.ports`, ""),
            t(`${i18nPrefix}.services.information.certificates`, ""),
            t(`${i18nPrefix}.services.information.device`, ""),
          ],
        },
      ],
    },
    {
      id: "event-management",
      title: t(`${i18nPrefix}.siem`, "Event Management (SIEM)"),
      icon: "securityApp",
      color: C.accent,
      features: [
        {
          icon: "securitySignal",
          title: t(`${i18nPrefix}.siem.events`, "Events"),
          description: t(`${i18nPrefix}.siem.events.description`, ""),
        },
        {
          icon: "aggregate",
          title: t(`${i18nPrefix}.siem.aggregation`, "Aggregation"),
          description: t(`${i18nPrefix}.siem.aggregation.description`, ""),
          details: [
            t(`${i18nPrefix}.siem.aggregation.same`, ""),
            t(`${i18nPrefix}.siem.aggregation.rule`, ""),
            t(`${i18nPrefix}.siem.aggregation.group`, ""),
          ],
        },
        {
          icon: "map",
          title: t(`${i18nPrefix}.siem.mitre`, "MITRE ATT&CK"),
          description: t(`${i18nPrefix}.siem.mitre.description`, ""),
        },
        {
          icon: "monitoring",
          title: t(`${i18nPrefix}.siem.devices`, "Affected Devices"),
          description: t(`${i18nPrefix}.siem.devices.description`, ""),
          details: [
            t(`${i18nPrefix}.siem.devices.affected`, ""),
            t(`${i18nPrefix}.siem.devices.impacted`, ""),
          ],
        },
        {
          icon: "logoSecurity",
          title: t(`${i18nPrefix}.siem.wazuh`, "Wazuh Integration"),
          description: t(`${i18nPrefix}.siem.wazuh.description`, ""),
          details: [
            t(`${i18nPrefix}.siem.wazuh.description`, ""),
            t(`${i18nPrefix}.siem.wazuh.hids`, ""),
            t(`${i18nPrefix}.siem.wazuh.log`, ""),
            t(`${i18nPrefix}.siem.wazuh.alert`, ""),
            t(`${i18nPrefix}.siem.wazuh.rule`, ""),
            t(`${i18nPrefix}.siem.wazuh.compliance`, ""),
          ],
        },
      ],
    },
    {
      id: "certificate-management",
      title: t(`${i18nPrefix}.certificates`, "Certificate Management"),
      icon: "lock",
      color: C.accent,
      features: [
        {
          icon: "document",
          title: t(`${i18nPrefix}.certificates.monitoring`, "Monitoring"),
          description: t(
            `${i18nPrefix}.certificates.monitoring.description`,
            "",
          ),
          details: [
            t(`${i18nPrefix}.certificates.monitoring.cn`, ""),
            t(`${i18nPrefix}.certificates.monitoring.dns`, ""),
            t(`${i18nPrefix}.certificates.monitoring.issuer`, ""),
            t(`${i18nPrefix}.certificates.monitoring.san`, ""),
          ],
        },
        {
          icon: "calendar",
          title: t(`${i18nPrefix}.certificates.time`, "Validity"),
          description: t(`${i18nPrefix}.certificates.time.description`, ""),
          details: [
            t(`${i18nPrefix}.certificates.time.from`, ""),
            t(`${i18nPrefix}.certificates.time.until`, ""),
            t(`${i18nPrefix}.certificates.time.status`, ""),
          ],
        },
        {
          icon: "monitor",
          title: t(`${i18nPrefix}.certificates.usecase`, "Use Cases"),
          description: t(`${i18nPrefix}.certificates.usecase.description`, ""),
        },
      ],
    },
    {
      id: "account-management",
      title: t(`${i18nPrefix}.accounts`, "Account Management"),
      icon: "user",
      color: C.accent,
      features: [
        {
          icon: "user",
          title: t(`${i18nPrefix}.accounts.info`, "Account Information"),
          description: t(`${i18nPrefix}.accounts.info.description`, ""),
          details: [
            t(`${i18nPrefix}.accounts.info.name`, ""),
            t(`${i18nPrefix}.accounts.info.status`, ""),
            t(`${i18nPrefix}.accounts.info.contact`, ""),
            t(`${i18nPrefix}.accounts.info.location`, ""),
            t(`${i18nPrefix}.accounts.info.company`, ""),
          ],
        },
        {
          icon: "key",
          title: t(`${i18nPrefix}.accounts.login`, "Login Activity"),
          description: t(`${i18nPrefix}.accounts.login.description`, ""),
          details: [
            t(`${i18nPrefix}.accounts.login.last`, ""),
            t(`${i18nPrefix}.accounts.login.password.changed`, ""),
            t(`${i18nPrefix}.accounts.login.failed`, ""),
          ],
        },
        {
          icon: "users",
          title: t(`${i18nPrefix}.accounts.groups`, "Groups"),
          description: t(`${i18nPrefix}.accounts.groups.description`, ""),
          details: [
            t(`${i18nPrefix}.accounts.groups.members`, ""),
            t(`${i18nPrefix}.accounts.groups.rights`, ""),
          ],
        },
        {
          icon: "logoSecurity",
          title: t(`${i18nPrefix}.accounts.ldap`, "LDAP"),
          description: t(`${i18nPrefix}.accounts.ldap.description`, ""),
        },
      ],
    },
    {
      id: "netflow",
      title: t(`${i18nPrefix}.netflow`, "NetFlow"),
      icon: "visTable",
      color: C.accent,
      features: [
        {
          icon: "visHeatmap",
          title: t(`${i18nPrefix}.netflow`, "NetFlow Analysis"),
          description: t(`${i18nPrefix}.netflow.description`, ""),
          details: [
            t(`${i18nPrefix}.netflow.visual`, ""),
            t(`${i18nPrefix}.netflow.anomaly`, ""),
            t(`${i18nPrefix}.netflow.correlation`, ""),
            t(`${i18nPrefix}.netflow.top`, ""),
          ],
        },
      ],
    },
  ];

  return (
    <div
      style={{
        background: C.pageBg,
        fontFamily: C.sans,
        color: C.textPrimary,
        padding: `64px clamp(16px, 4vw, 48px)`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 32 : 56 }}>
          <Eyebrow>{t(`${i18nPrefix}`, "Platform Features")}</Eyebrow>
          <div style={{ width: "fit-content", maxWidth: "100%", margin: "0 auto 20px" }}>
            <EuiTitle size={isMobile ? "m" : "l"}>
              <h2
                style={{
                  color: C.textPrimary,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  marginBottom: 0,
                }}
              >
                {t(`${i18nPrefix}`, "Platform Features")}
              </h2>
            </EuiTitle>
            <div
              style={{
                width: "100%",
                height: 3,
                marginTop: 16,
                background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
                borderRadius: 2,
              }}
            />
          </div>
          <EuiText color="subdued" style={{ maxWidth: 680, margin: "0 auto" }}>
            <p
              style={{
                fontSize: isMobile ? "0.9rem" : "1rem",
                lineHeight: 1.75,
              }}
            >
              {t(
                `${i18nPrefix}.description`,
                "A comprehensive security and observability platform for your entire infrastructure.",
              )}
            </p>
          </EuiText>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 8 : 12,
          }}
        >
          {featureCategories.map((category, i) => (
            <CategoryAccordion
              key={category.id}
              category={category}
              defaultOpen={i === 0}
            />
          ))}
        </div>

        <EuiSpacer size="xxl" />
        <EuiHorizontalRule margin="none" />
        <EuiSpacer size="xxl" />

        <div>
          <Eyebrow>{t(`${i18nPrefix}.card.eyebrow`, "Why opsanio")}</Eyebrow>
          <EuiTitle size={isMobile ? "s" : "m"}>
            <h3
              style={{
                color: C.textPrimary,
                fontWeight: 800,
                marginBottom: 28,
                letterSpacing: "-0.01em",
              }}
            >
              {t(`${i18nPrefix}.card.title`, "Built for modern security teams")}
            </h3>
          </EuiTitle>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {SUMMARY_CARDS.map((card) => (
              <SummaryCardItem
                key={card.titleKey}
                card={card}
                translate={translate}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernFeaturesPanel;
